/**
 * Which drill-down is open, held in the URL.
 *
 * `routes/Lcfs.tsx` renders `<Tabs keepMounted={false}>`, so only the visible
 * section is mounted and component state dies on a section switch. A drawer held
 * in `useState` would therefore vanish on a switch and never come back — and
 * "URL is state of record" is a locked convention here anyway, so a reader can
 * send someone a link to the station they are looking at.
 *
 * Writes go through `withParams`, the pure helper, rather than
 * `batch-detail/useParamPatcher`. That hook exists to let two independently
 * polling job handles compose their writes within one tick; nothing here writes
 * except a click, and one click is one write.
 *
 * `station` and `dairy` are deliberately NOT in `TAB_PARAMS` (`routes/Lcfs.tsx`).
 * `setActive` rebuilds the query string from that map on every section switch,
 * so leaving them out is exactly what closes the drawer when the reader moves to
 * another section. A pasted `?tab=fse&station=FSE10317NS21` still restores it,
 * because `setActive` only runs on a switch and never on mount.
 */

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { withParams } from "../../lib/searchParams";

export function useLcfsDrilldown() {
  const [params, setParams] = useSearchParams();
  const stationId = params.get("station");
  const dairyId = params.get("dairy");

  const patch = useCallback(
    (updates: Record<string, string | null>) =>
      setParams((prev) => withParams(prev, updates), { replace: true }),
    [setParams],
  );

  return {
    stationId,
    dairyId,
    // Opening one side closes the other in the same write, so the two drawers
    // can never be open at once and a cross-link is a single history entry.
    openStation: useCallback((fseId: string) => patch({ station: fseId, dairy: null }), [patch]),
    openDairy: useCallback((id: string) => patch({ dairy: id, station: null }), [patch]),
    closeStation: useCallback(() => patch({ station: null }), [patch]),
    closeDairy: useCallback(() => patch({ dairy: null }), [patch]),
  };
}
